// DetailPage.js
// 内容详情页：展示单条内容的全部信息（标题、图片、内容、地址、联系方式等）
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, Image, Spin, Tag, Descriptions, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import axios from 'axios';
import { BASE_URL } from './Config.js';
import { formatDate, toHttpsUrl } from './Util.js';
import './DetailPage.css';

const DetailPage = ({ overlay = false }) => {
  const { type, id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const initialState = location.state || {};
  const isLf = type === 'lf';
  const area = initialState.area || new URLSearchParams(location.search).get('area') || '1';

  const [record, setRecord] = useState(initialState.record || null);
  const [loading, setLoading] = useState(!initialState.record);

  // 浮层模式：锁住背后列表页的滚动，并记录滚动位置，返回时原样恢复
  useEffect(() => {
    if (!overlay) {
      return undefined;
    }
    const scrollY = window.scrollY;
    const body = document.body;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
    };
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.left = prev.left;
      body.style.right = prev.right;
      body.style.width = prev.width;
      window.scrollTo(0, scrollY);
    };
  }, [overlay]);

  useEffect(() => {
    // 从列表页跳转时已经带上了数据，直接展示；刷新页面时再按 id 拉取
    if (initialState.record) {
      return;
    }

    let url = isLf
      ? `${BASE_URL}/xhg/getLfContent?area_id=${area}&page_no=1&page_size=10&id=${id}`
      : `${BASE_URL}/xhg/getKeepContent?page_no=1&page_size=10&id=${id}`;

    setLoading(true);
    axios
      .get(url)
      .then((response) => {
        const list = (response.data && response.data.data && response.data.data.lf_content) || [];
        const found = list.find((item) => String(item.id) === String(id)) || list[0];
        if (found) {
          setRecord({
            ...found,
            create_time_text: formatDate(found.create_time),
            update_time_text: formatDate(found.update_time),
          });
        } else {
          setRecord(null);
        }
      })
      .catch((error) => {
        message.error('获取详情失败: ' + error, 5);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, id, area]);

  const images = record && Array.isArray(record.img_urls)
    ? record.img_urls.filter((url) => url && String(url).trim() !== '').map(toHttpsUrl)
    : [];

  const safeContent =
    record && typeof record.content === 'string'
      ? record.content.replace(/\\n/g, '\n')
      : record && record.content;

  const goBack = () => {
    // 直接从详情链接进入（无历史记录）时，退回对应列表页
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(isLf ? '/lfContent' : '/byContent');
    }
  };

  return (
    <div className={overlay ? 'detail-page detail-page--overlay' : 'detail-page'}>
      <div className="detail-page-inner">
        <Button
          className="detail-back"
          icon={<ArrowLeftOutlined />}
          onClick={goBack}
        >
          返回
        </Button>

        {loading ? (
          <div className="detail-loading">
            <Spin />
          </div>
        ) : !record ? (
          <div className="detail-empty">未找到该内容</div>
        ) : (
          <>
            <h2 className="detail-title">{record.title || '（无标题）'}</h2>

            <div className="detail-tags">
              {record.price !== undefined && record.price !== null && String(record.price).trim() !== '' && (
                <Tag color="red">￥{record.price}</Tag>
              )}
              {record.location && <Tag color="blue">{record.location}</Tag>}
              {isLf && record.location_detail && <Tag>{record.location_detail}</Tag>}
            </div>

            {images.length > 0 && (
              <Image.PreviewGroup>
                <div className="detail-images">
                  {images.map((url, index) => (
                    <Image
                      key={`img_${index}`}
                      rootClassName="detail-image-box"
                      className="detail-image"
                      src={url}
                      alt={`图片${index + 1}`}
                    />
                  ))}
                </div>
              </Image.PreviewGroup>
            )}

            {safeContent && (
              <div className="detail-section">
                <div className="detail-section-title">内容</div>
                <div className="detail-content">{safeContent}</div>
              </div>
            )}

            <Descriptions column={1} size="small" bordered className="detail-desc">
              <Descriptions.Item label="联系方式">{record.contact || '-'}</Descriptions.Item>
              <Descriptions.Item label="地点">{record.location || '-'}</Descriptions.Item>
              {isLf && (
                <Descriptions.Item label="详细地址">{record.location_detail || '-'}</Descriptions.Item>
              )}
              <Descriptions.Item label="tid">
                {record.tid !== undefined && record.tid !== null ? record.tid : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {record.create_time_text || formatDate(record.create_time) || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="更新时间">
                {record.update_time_text || formatDate(record.update_time) || '-'}
              </Descriptions.Item>
            </Descriptions>
          </>
        )}
      </div>
    </div>
  );
};

export default DetailPage;
